import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryParams } from '@/@diffrag/hooks/useQueryParams'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { TableFormatter } from '@/@diffrag/table/TableFormatter'
import { api } from '@/api'
import { ArticleTypeEnum } from 'api'
import { useState } from 'react'
import { Button } from '@/components/ui/button'

const ARTICLE_TYPE_OPTIONS = [
  { label: '체험단 구인구직', value: ArticleTypeEnum.ClinicExperience },
  { label: '리뷰', value: ArticleTypeEnum.ClinicReview },
  { label: '커뮤니티', value: ArticleTypeEnum.CommunityArticle },
]

const STATUS_OPTIONS = [
  { label: '전체', value: undefined },
  { label: '활성', value: 'ACTIVE' },
  { label: '숨김처리', value: 'INACTIVE' },
]

export default function Comment() {
  const page = useQueryValue('page', 1)
  const q = useQueryValue('q', undefined)
  const { query } = useQueryParams<{
    articleType?:
      | ArticleTypeEnum.ClinicExperience
      | ArticleTypeEnum.ClinicReview
      | ArticleTypeEnum.CommunityArticle
  }>({
    articleType: undefined,
  })

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    undefined,
  )
  const [selectedArticleType, setSelectedArticleType] = useState<
    ArticleTypeEnum | undefined
  >(ArticleTypeEnum.ClinicExperience)

  const articleType = selectedArticleType || query.articleType
  const { data, isFetching } = useApi({
    id: 'commentList',
    fn: api().adminComment.getCommentList,
    params: {
      page,
      q,
      pageSize: 10,
      status: selectedStatus as never,
      articleType: articleType!,
    },
  })

  const handleStatusSelect = (status: string | undefined) => {
    setSelectedStatus(status)
  }

  const handleArticleTypeSelect = (type: ArticleTypeEnum | undefined) => {
    setSelectedArticleType(type)
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="flex gap-2">
            {ARTICLE_TYPE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={
                  selectedArticleType === option.value ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => handleArticleTypeSelect(option.value)}
                className="min-w-[120px]"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">상태:</span>
          <div className="flex gap-2">
            {STATUS_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={
                  selectedStatus === option.value ? 'default' : 'outline'
                }
                size="sm"
                onClick={() => handleStatusSelect(option.value)}
                className="min-w-[100px]"
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      <SimpleTable
        isLoading={isFetching}
        data={data?.rows}
        options={{
          disableSelect: true,
          disableNew: true,
          disableSort: true,
        }}
        columns={[
          {
            key: 'id',
            label: 'NO',
            isPrimary: true,
            width: 70,
            href: ({ id, article }) =>
              `/comment/detail?id=${id}&articleId=${article.id}`,
          },
          {
            key: 'article.type',
            label: '게시글 타입',
            formatter: (value) => {
              return (
                ARTICLE_TYPE_OPTIONS.find((option) => option.value === value)
                  ?.label || '-'
              )
            },
            width: 130,
          },
          {
            label: '게시글 제목',
            key: 'article.title',
            formatter: (value) => {
              return value && value.length > 30
                ? value.substring(0, 30) + '...'
                : value || '-'
            },
            isPrimary: true,
            href: ({ id, article }) => `/community/edit?id=${article.id}`,
          },
          {
            key: 'author',
            label: '작성자',
            formatter: (value) => {
              return value?.clinic?.name || value?.userData?.nickname || '-'
            },
          },
          {
            label: '댓글 내용',
            key: 'content',
            isPrimary: true,
            href: ({ id, article }) =>
              `/comment/detail?id=${id}&articleType=${articleType}&articleId=${article.id}`,
            formatter: (value) => {
              return value && value.length > 50
                ? value.substring(0, 50) + '...'
                : value || '-'
            },
          },

          {
            label: '작성일시',
            key: 'createDate',
            formatter: TableFormatter.datetime(),
          },
          {
            label: '상태',
            key: 'active',
            formatter: (value) => {
              return value ? '활성' : '숨김처리'
            },
          },
        ]}
      />
      <OffsetPagination page={page} total={data?.count} pageSize={20} />
    </ContentView>
  )
}
